import { Fragment, useRef, useState } from "react";
import Layout from "@/components/Layout";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadCloud, FileDown, CheckCircle2, AlertCircle, FileText, UserPlus, PhoneCall, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CLASSES = ["Play Group", "LKG", "UKG", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
const SECTIONS = ["A", "B"];

export default function AdminStudents({ embedded } = {}) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragging, setDragging] = useState(false);

  // Single-student create form
  const blank = { student_id: "", roll_number: "", name: "", class_name: "5", section: "A", gender: "", dob: "", parent_name: "", parent_phone: "", address: "" };
  const [single, setSingle] = useState(blank);
  const [creating, setCreating] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);

  const setF = (k, v) => setSingle((s) => ({ ...s, [k]: v }));

  const createOne = async (e) => {
    e?.preventDefault?.();
    if (!single.student_id || !single.roll_number || !single.name || !single.class_name || !single.section) {
      return toast.error("Student ID, roll number, name, class and section are required.");
    }
    if (!/^\d{9}$/.test(single.student_id)) {
      return toast.error("Student ID must be a 9-digit number.");
    }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/students", single);
      setLastCreated(data);
      toast.success(`Added ${data.name} (${data.roll_number})`);
      setSingle({ ...blank, class_name: single.class_name, section: single.section });
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not create student");
    } finally { setCreating(false); }
  };

  const pick = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please pick a .csv file");
      return;
    }
    setFile(f); setResult(null);
  };

  const submit = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/students/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Imported ${data.created} students${data.skipped_duplicates ? `, ${data.skipped_duplicates} skipped` : ""}`);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Import failed");
    } finally { setUploading(false); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/students/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url; link.setAttribute("download", "students_template.csv");
      document.body.appendChild(link); link.click(); link.remove();
    } catch (err) {
      console.error("download template failed:", err);
      toast.error("Could not download template");
    }
  };

  const Wrap = embedded ? Fragment : Layout;
  return (
    <Wrap>
      <div className="max-w-3xl">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Administration</div>
        <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily:"Outfit"}}>Add students</h1>
        <p className="text-slate-500 mt-1 mb-6">Create one student at a time, or upload a CSV for bulk import. New roll numbers are unique per school.</p>

        {/* Add a single student */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-5" data-testid="single-student-panel">
          <div className="flex items-center gap-2 mb-1 text-sky-700">
            <UserPlus className="h-4 w-4" />
            <span className="text-xs uppercase tracking-[0.2em]">Add a single student</span>
          </div>
          <h2 className="text-lg font-semibold text-slate-900" style={{fontFamily:"Outfit"}}>One-off entry</h2>
          <p className="text-xs text-slate-500 mb-4">Useful for ad-hoc additions or for testing the absent-call flow with a real phone number.</p>

          <form onSubmit={createOne} className="grid sm:grid-cols-2 gap-4" data-testid="single-student-form">
            <Field label="Student ID * (9 digits)" testid="ss-sid">
              <Input className="h-10 rounded-lg" placeholder="e.g. 100000099" inputMode="numeric" maxLength={9} value={single.student_id}
                onChange={(e) => setF("student_id", e.target.value.replace(/\D/g, "").slice(0, 9))} data-testid="ss-sid-input"/>
            </Field>
            <Field label="Roll number *" testid="ss-roll">
              <Input className="h-10 rounded-lg uppercase" placeholder="e.g. 5A99" value={single.roll_number}
                onChange={(e) => setF("roll_number", e.target.value)} data-testid="ss-roll-input"/>
            </Field>
            <Field label="Full name *" testid="ss-name">
              <Input className="h-10 rounded-lg" placeholder="e.g. Aanya Kapoor" value={single.name}
                onChange={(e) => setF("name", e.target.value)} data-testid="ss-name-input"/>
            </Field>
            <Field label="Class *" testid="ss-class">
              <Select value={single.class_name} onValueChange={(v) => setF("class_name", v)}>
                <SelectTrigger className="h-10 rounded-lg" data-testid="ss-class-trigger"><SelectValue/></SelectTrigger>
                <SelectContent>{CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Section *" testid="ss-section">
              <Select value={single.section} onValueChange={(v) => setF("section", v)}>
                <SelectTrigger className="h-10 rounded-lg" data-testid="ss-section-trigger"><SelectValue/></SelectTrigger>
                <SelectContent>{SECTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Gender" testid="ss-gender">
              <Select value={single.gender || "unspec"} onValueChange={(v) => setF("gender", v === "unspec" ? "" : v)}>
                <SelectTrigger className="h-10 rounded-lg" data-testid="ss-gender-trigger"><SelectValue placeholder="—"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unspec">—</SelectItem>
                  <SelectItem value="F">Female</SelectItem>
                  <SelectItem value="M">Male</SelectItem>
                  <SelectItem value="O">Other</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Date of birth (YYYY-MM-DD)" testid="ss-dob">
              <DatePicker className="rounded-lg" value={single.dob}
                onChange={(v) => setF("dob", v)} data-testid="ss-dob-input"/>
            </Field>
            <Field label="Parent name" testid="ss-parent">
              <Input className="h-10 rounded-lg" placeholder="e.g. Meera Kapoor" value={single.parent_name}
                onChange={(e) => setF("parent_name", e.target.value)} data-testid="ss-parent-input"/>
            </Field>
            <Field label="Parent phone (for absent calls)" testid="ss-phone">
              <Input className="h-10 rounded-lg font-mono" placeholder="+91 9876543210" value={single.parent_phone}
                onChange={(e) => setF("parent_phone", e.target.value)} data-testid="ss-phone-input"/>
            </Field>
            <Field label="Address" testid="ss-addr" wide>
              <Input className="h-10 rounded-lg" placeholder="optional" value={single.address}
                onChange={(e) => setF("address", e.target.value)} data-testid="ss-addr-input"/>
            </Field>

            <div className="sm:col-span-2 flex items-center justify-between">
              <p className="text-xs text-slate-400">A real, reachable phone (E.164 / 10-digit Indian) is needed to trigger a Retell call.</p>
              <Button type="submit" disabled={creating} className="h-10 rounded-full bg-sky-500 hover:bg-sky-600" data-testid="ss-submit-btn">
                <UserPlus className="h-4 w-4 mr-1" /> {creating ? "Adding…" : "Add student"}
              </Button>
            </div>
          </form>

          {lastCreated && (
            <div className="mt-4 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm flex items-start gap-2" data-testid="ss-success">
              <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium">{lastCreated.name} added (#{lastCreated.roll_number}, {lastCreated.class_name}-{lastCreated.section})</div>
                <div className="text-xs mt-0.5 flex items-center gap-1">
                  <PhoneCall className="h-3 w-3"/> Parent phone on file: <span className="font-mono">{lastCreated.parent_phone || "—"}</span>
                </div>
                <div className="text-xs mt-1 text-emerald-700">
                  Next: open <b>Take Attendance</b> → pick class <b>{lastCreated.class_name}-{lastCreated.section}</b> → mark this student <b>absent</b> → Submit. A Retell call will be queued automatically.
                </div>
              </div>
            </div>
          )}
        </div>

        <h2 className="text-lg font-semibold text-slate-900 mb-3 mt-8" style={{fontFamily:"Outfit"}}>Or upload a CSV</h2>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-sm font-semibold text-slate-900" style={{fontFamily:"Outfit"}}>Need the format?</div>
              <div className="text-xs text-slate-500">
                Required columns: <span className="font-mono text-slate-700">student_id, roll_number, name, class_name, section</span>.
                Optional: gender, dob, parent_name, parent_phone, address.
              </div>
            </div>
            <Button variant="outline" onClick={downloadTemplate} className="rounded-full" data-testid="download-template-btn">
              <FileDown className="h-4 w-4 mr-1" /> Download template
            </Button>
          </div>

          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files?.[0]); }}
            onClick={() => inputRef.current?.click()}
            className={`cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
              dragging ? "border-sky-400 bg-sky-50" : "border-slate-200 bg-slate-50/60 hover:bg-slate-50"
            }`}
            data-testid="csv-dropzone"
          >
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => pick(e.target.files?.[0])}
              data-testid="csv-file-input"
            />
            <div className="h-12 w-12 mx-auto rounded-2xl bg-sky-100 text-sky-700 flex items-center justify-center mb-3">
              <UploadCloud className="h-6 w-6" />
            </div>
            {file ? (
              <div className="text-slate-700">
                <div className="font-medium flex items-center justify-center gap-2">
                  <FileText className="h-4 w-4" /> {file.name}
                </div>
                <div className="text-xs text-slate-500 mt-1">{(file.size / 1024).toFixed(1)} KB · click to change</div>
              </div>
            ) : (
              <>
                <div className="text-slate-900 font-medium">Drop your CSV here</div>
                <div className="text-sm text-slate-500 mt-1">or click to browse</div>
              </>
            )}
          </div>

          <div className="mt-5 flex justify-end gap-2">
            {file && (
              <Button variant="ghost" onClick={() => { setFile(null); setResult(null); if (inputRef.current) inputRef.current.value = ""; }} className="rounded-full" data-testid="clear-file-btn">
                <Trash2 className="h-4 w-4 mr-1" /> Clear
              </Button>
            )}
            <Button onClick={submit} disabled={!file || uploading} className="rounded-full bg-sky-500 hover:bg-sky-600" data-testid="csv-import-btn">
              {uploading ? "Importing…" : "Import students"}
            </Button>
          </div>
        </div>

        {result && (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="import-result">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              <div className="text-sm font-semibold text-slate-900" style={{fontFamily:"Outfit"}}>Import summary</div>
            </div>
            <div className="grid sm:grid-cols-3 divide-x divide-slate-100">
              <Tile label="Created" value={result.created} tone="emerald" />
              <Tile label="Skipped duplicates" value={result.skipped_duplicates} tone="slate" />
              <Tile label="Errors" value={result.error_count} tone={result.error_count ? "rose" : "slate"} />
            </div>
            {result.errors && result.errors.length > 0 && (
              <div className="px-5 py-4 border-t border-slate-100">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2 flex items-center gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5" /> Errors (first 50)
                </div>
                <ul className="divide-y divide-slate-100 max-h-64 overflow-y-auto text-sm" data-testid="import-errors">
                  {result.errors.map((err, i) => (
                    <li key={`${err.row}-${i}`} className="py-1.5 flex gap-3">
                      <span className="font-mono text-xs text-slate-400 w-16 shrink-0">Row {err.row}</span>
                      <span className="text-rose-600">{err.error}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </Wrap>
  );
}

function Tile({ label, value, tone }) {
  const map = {
    emerald: "text-emerald-700",
    rose: "text-rose-600",
    slate: "text-slate-700",
  };
  return (
    <div className="p-5 text-center">
      <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">{label}</div>
      <div className={`text-3xl font-bold ${map[tone]}`} style={{fontFamily:"Outfit"}}>{value}</div>
    </div>
  );
}

function Field({ label, children, wide, testid }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""} data-testid={testid}>
      <Label className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
